/* istanbul ignore file */
import { FormRow, FormSection, BigFormRow } from 'framework/ui/layout';
import { useEffect } from 'react';
import {
  EnumDropdown,
  BooleanDropdownOptionInput,
  ClearAfterUnmountWrapper,
  NumberInput,
  TextArea,
  DateInput,
  WysiwygInput,
  useFormContext,
  ClearAfterUnmountWrapperCustomized,
} from 'framework/form';
import { useOpportunityId } from 'domain/shared/opportunity';
import {
  APPLICATION_TYPE,
  MOBILE_OS,
  PRESALE_REQUEST_TYPE,
  REQUEST_STATUS_ALIAS,
} from 'domain/sales/opportunity/presale/request/shared/presaleRequestConstants';
import {
  showApplicationTypeField,
  showCollaborationExpectationFiled,
  showHasAutomationTestingField,
  showMaterialsField,
  showMobileOSField,
  showPercentTestCoverageField,
  showReplacementReasonField,
  showRequiredFteField,
  showScopeOfWorkField,
  showSystemCompletionField,
  showSystemHostingLocationField,
  showTeamInvolvementField,
  showThirdPartyField,
} from 'domain/sales/opportunity/presale/request/shared/presaleRequestFieldVisibilityHelper';
import { ARTIFACT_ID } from 'domain/shared/artifact';
import CompositeDocumentUploaderInput from 'domain/sales/opportunity/shared/storage/components/CompositeDocumentUploaderInput';
import usePresaleOpportunityService from '../shared/presaleRequestService';

const MATERIAL_HELP_TEXT =
  '* High-level overview\n' +
  '* Source code of current product version\n' +
  '* Access to deployed environment\n' +
  '* List of new features\n' +
  '* List of existing known issues\n' +
  '* Technical documentation';

const PresaleRequestForm = ({ isCreateForm }: { isCreateForm?: boolean }) => {
  const { data, setFormData, formErrors } = useFormContext();
  const { getLastPresaleRequest, mapInitialData } = usePresaleOpportunityService();
  const opportunityId = useOpportunityId();

  useEffect(() => {
    (async () => {
      if (isCreateForm) {
        const lastOutput = await getLastPresaleRequest(opportunityId);
        if (lastOutput != null && setFormData) {
          const initialData = mapInitialData(lastOutput);
          setFormData(initialData);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunityId]);

  useEffect(() => {
    if (formErrors && formErrors.requestStatus && setFormData && data?.requestStatus !== 'DRAFT') {
      setFormData({
        ...data,
        requestStatus: 'DRAFT',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formErrors]);

  return (
    <FormSection>
      <FormRow>
        <EnumDropdown options={REQUEST_STATUS_ALIAS} source="requestStatus" disabled label="Request status" />
      </FormRow>
      <FormRow>
        <EnumDropdown
          options={PRESALE_REQUEST_TYPE}
          source="requestType"
          label="Request type"
          placeholder="Select..."
        />
        <ClearAfterUnmountWrapper isVisible={showSystemCompletionField}>
          <NumberInput source="systemCompletionProgress" label="System completion" />
        </ClearAfterUnmountWrapper>
        <ClearAfterUnmountWrapper isVisible={showTeamInvolvementField}>
          <BooleanDropdownOptionInput
            source="hasPreviousTeamInvolvement"
            label="Previous team involvement"
            placeholder="Select..."
          />
        </ClearAfterUnmountWrapper>
        <DateInput source="expectedCompletionAt" label="Expected completion at" />
        <ClearAfterUnmountWrapper isVisible={showHasAutomationTestingField}>
          <BooleanDropdownOptionInput
            source="hasAutomatedTesting"
            label="Automated testing coverage"
            placeholder="Select..."
          />
        </ClearAfterUnmountWrapper>
        <ClearAfterUnmountWrapper isVisible={showPercentTestCoverageField}>
          <NumberInput source="automatedTestingCoverage" label="% of coverage" />
        </ClearAfterUnmountWrapper>
        <ClearAfterUnmountWrapper isVisible={showApplicationTypeField}>
          <EnumDropdown
            options={APPLICATION_TYPE}
            source="applicationTypes"
            multiple
            label="Application types"
            placeholder="Select..."
            helpText="Select one or more options"
          />
        </ClearAfterUnmountWrapper>
        <ClearAfterUnmountWrapper isVisible={showMobileOSField}>
          <EnumDropdown
            options={MOBILE_OS}
            source="mobileOperationSystems"
            multiple
            label="Mobile OS"
            placeholder="Select..."
            helpText="Select one or more options"
          />
        </ClearAfterUnmountWrapper>
        <ClearAfterUnmountWrapper isVisible={showThirdPartyField}>
          <BooleanDropdownOptionInput
            source="hasThirdPartyIntegrations"
            label="Third-Party integration"
            placeholder="Select..."
          />
        </ClearAfterUnmountWrapper>
        <ClearAfterUnmountWrapper isVisible={showRequiredFteField}>
          <NumberInput source="requiredFte" label="Required FTE" />
        </ClearAfterUnmountWrapper>
      </FormRow>

      <BigFormRow>
        <ClearAfterUnmountWrapper isVisible={showCollaborationExpectationFiled}>
          <TextArea source="collaborationExpectations" label="Collaboration expectations" />
        </ClearAfterUnmountWrapper>
      </BigFormRow>

      <BigFormRow>
        <ClearAfterUnmountWrapper isVisible={showSystemHostingLocationField}>
          <TextArea source="systemHostingLocation" label="System hosting location" />
        </ClearAfterUnmountWrapper>
      </BigFormRow>

      <BigFormRow>
        <ClearAfterUnmountWrapper isVisible={showReplacementReasonField}>
          <TextArea source="replacementReason" label="Replacement reason" />
        </ClearAfterUnmountWrapper>
      </BigFormRow>

      <BigFormRow>
        <ClearAfterUnmountWrapper isVisible={showScopeOfWorkField}>
          <TextArea source="scopeOfWork" label="Scope of work" />
        </ClearAfterUnmountWrapper>
      </BigFormRow>

      <BigFormRow>
        <ClearAfterUnmountWrapper isVisible={showMaterialsField}>
          <WysiwygInput source="materials" label="Description" helpText={MATERIAL_HELP_TEXT} />
        </ClearAfterUnmountWrapper>
        <ClearAfterUnmountWrapperCustomized
          isVisible={showMaterialsField}
          getSources={props => [props.artifactsListSource, props.documentsListsSource]}
        >
          <CompositeDocumentUploaderInput
            label="Attachments"
            artifactTypeId={ARTIFACT_ID.CRM_ATTACHMENT}
            artifactsListSource={'attachmentsIds'}
            documentsListsSource={'artifactPaths'}
          />
        </ClearAfterUnmountWrapperCustomized>
      </BigFormRow>
    </FormSection>
  );
};

export default PresaleRequestForm;
